import "./SoundboxHeader.scss";
import { IconButton } from "../IconButton/IconButton";
import { ReactComponent as HamurgerIcon } from "../../static/icons/hamburger.svg";
import { Separator } from "../Separator/Separator";
import { ReactComponent as SoundIcon } from "../../static/icons/sound.svg";
import { ReactComponent as TrunOnOffIcon } from "../../static/icons/turnOnOff.svg";
import { ReactComponent as NightIcon } from "../../static/icons/night.svg";
import { ReactComponent as CloseIcon } from "../../static/icons/close.svg";
import { SwitchButton } from "../SwitchButton/SwitchButton";
import classNames from "classnames";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Tag } from "../Tag/Tag";
import { Carousel } from "../Carousel/Carousel";
import { Button } from "../Button/Button";
import { PageDataContext } from "../../context/PageDataContext";
import { SelectedHeatPumpContext, SelectedHeatPumpData } from "../../context/SelectedHeatPumpContext";
import { LanguagePicker } from "../LanguagePicker/LanguagePicker";
import { TextButton } from "../TextButton/TextButton";
import { ReactComponent as SoundMaxIcon } from "../../static/icons/soundMax.svg";
import { Category } from "../../types/category";
import React from "react";
import { useGetCurrentScenes } from "../../hooks/useGetCurrentScenes";
import { Disclaimer } from "../Disclaimer/Disclaimer";
import { Loading } from "../Loading/Loading";
import { useLoadingDataDelay } from "../../hooks/useLoadingDataDelay";
import { checkIfIsMoreThanOneCategory } from "../../utils/checkIfIsMoreThanOneCategory";
import { useGetAvailableLanguages } from "../../hooks/useGetAvailableLanguages";
import { Scene } from "../../types/heatPump";
import { SceneDescription } from "../../types/languages";
import { useFindHeatPumpTranslation } from "../../hooks/useFindHeatPumpTranslation";
import { Surroundings } from "../../types/surroundings";

type SoundboxHeaderProps = {
  turnOnSound: boolean;
  nightMode: boolean;
  setTurnOnSound: React.Dispatch<React.SetStateAction<boolean>>;
  setNightMode: React.Dispatch<React.SetStateAction<boolean>>;
  setOverlayRestart: React.Dispatch<React.SetStateAction<boolean>>;
  activeIndex: number;
  loadingHeatPumpSound: boolean;
  surrounding: Surroundings;
};

export const SoundboxHeader = React.memo((props: SoundboxHeaderProps) => {
  const context = useContext(PageDataContext);
  const selectedHeatPumpContext = useContext(SelectedHeatPumpContext);
  const [open, setOpen] = useState<boolean>(false);
  const soundboxOptionsRef = useRef<HTMLDivElement>(null);
  const [category, setCategory] = useState<Category>("all");
  const [carouselHeatPumpSelection, setCarouselHeatPumpSelection] = useState<{
    selectedHeatPumpId: number;
    variantIndex: number;
  }>({
    selectedHeatPumpId: selectedHeatPumpContext.data.heatPumpId,
    variantIndex: selectedHeatPumpContext.data.variantIndex,
  });
  const currentScenes = useGetCurrentScenes();
  const [openDisclaimer, setOpenDisclaimer] = useState<boolean>(false);
  const { showLoading } = useLoadingDataDelay(props.loadingHeatPumpSound);
  const { availableLanguages } = useGetAvailableLanguages();
  const { heatPumpTranslation } = useFindHeatPumpTranslation(context, selectedHeatPumpContext.data);

  const showTags = useMemo(() => checkIfIsMoreThanOneCategory(context.heatPumps), [context.heatPumps]);

  useEffect(() => {
    const heatPump = context.heatPumps.find(e => e.id === carouselHeatPumpSelection.selectedHeatPumpId)?.variants[
      carouselHeatPumpSelection.variantIndex
    ];

    if (heatPump) {
      (window as any).dataLayer.push({
        event: "hp_scene",
        step: props.activeIndex + 1,
        hp_model: heatPump.shortcut,
        power_setting: heatPump.power + " kW",
        background_noise: props.surrounding,
        distance: currentScenes[props.activeIndex].distance + "m",
        // hp_location: currentScenes[props.activeIndex].outside ? "Indoor" : "Outdoor",
        hp_location: "Outdoor",
      });
    }
  }, [props.activeIndex]);

  const heatPumps = useMemo(() => {
    if (category === "water") {
      return context.heatPumps.filter(e => e.category === "water");
    } else if (category === "air") {
      return context.heatPumps.filter(e => e.category === "air");
    } else if (category === "floor") {
      return context.heatPumps.filter(e => e.category === "floor");
    }

    return context.heatPumps;
  }, [category, context.heatPumps]);

  const soundboxLogo = (className?: string) => {
    return (
      <div
        className={classNames("soundbox-logo", className)}
        onClick={() => props.setOverlayRestart(true)}
        tabIndex={0}
        role="button"
      >
        {vaillantLogo}
      </div>
    );
  };

  const varaintCounts = useMemo(() => {
    return {
      all: context.heatPumps.length,
      water: context.heatPumps.filter(e => e.category === "water").length,
      air: context.heatPumps.filter(e => e.category === "air").length,
      floor: context.heatPumps.filter(e => e.category === "floor").length,
    };
  }, [context.heatPumps]);

  const translateVariantName = () => {
    return heatPumpTranslation ? heatPumpTranslation.variantText : "";
  };

  const translateCurrenScene = (scene: Scene) => {
    if (!scene || !heatPumpTranslation) {
      return "";
    }

    const findTranslationData = (sceneTranslation: Array<SceneDescription>) => {
      const sceneTranslationData = sceneTranslation.find(e => e.distance === scene.distance);
      return sceneTranslationData ? sceneTranslationData.text : "";
    };

    if (scene.summerImage === true && heatPumpTranslation?.scenesSummer) {
      return findTranslationData(heatPumpTranslation?.scenesSummer);
    } else if (scene.summerImage === false && heatPumpTranslation?.scenesWinter) {
      return findTranslationData(heatPumpTranslation?.scenesWinter);
    }

    return "";
  };

  return (
    <>
      <div className={classNames("soundbox-header-wrapper", open && "soundbox-header-wrapper--menu-open")}>
        <header className="soundbox-header">
          <div className="soundbox-header__avatar">
            {soundboxLogo()}
            <span className="soundbox-header__text">{context.translations.soundbox.logoTitle}</span>
          </div>
          <div className="soundbox-header__menu">
            <div className="soundbox-options" ref={soundboxOptionsRef}>
              {soundboxLogo("soundbox-logo--small-screen")}
              <div className="product-specification-wrapper">
                <div className="product-specification">
                  <span className="product-specification__name">{heatPumpTranslation && heatPumpTranslation.name}</span>
                  <span className="product-specification__description">{translateVariantName()}</span>
                </div>
                <IconButton className="soundbox-options__button" onClick={() => setOpen(open => !open)}>
                  {open ? <CloseIcon /> : <HamurgerIcon />}
                </IconButton>
              </div>
            </div>

            <Separator className="soundbox-header__separator" />
            <div className={classNames("sound-options", open && "sound-options--hidden")}>
              <div className="sound-options-description">
                <span className="sound-options-description__title">
                  <span className={classNames("sound-option-icon", props.turnOnSound && "sound-option-icon--active")}>
                    {showLoading ? <Loading /> : <SoundIcon />}
                  </span>

                  {currentScenes[props.activeIndex] && currentScenes[props.activeIndex].outside
                    ? context.translations.soundbox.outsideUnit
                    : context.translations.soundbox.indoorUnit}
                </span>
                <span className="sound-options-description__subtitle">
                  {translateCurrenScene(currentScenes[props.activeIndex])}
                </span>
              </div>
              <div className="sound-options__buttons">
                <SwitchButton
                  active={props.nightMode}
                  onClick={() => {
                    props.setNightMode(e => !e);
                    const heatPump = context.heatPumps.find(e => e.id === carouselHeatPumpSelection.selectedHeatPumpId)
                      ?.variants[carouselHeatPumpSelection.variantIndex];

                    if (heatPump) {
                      (window as any).dataLayer.push({
                        event: "hp_night_mode",
                        hp_model: heatPump.shortcut,
                        power_setting: heatPump.power + " kW",
                      });
                    }
                  }}
                  disabled={!currentScenes[props.activeIndex]?.silentMode}
                >
                  <NightIcon />
                </SwitchButton>

                <SwitchButton active={props.turnOnSound} onClick={() => props.setTurnOnSound(e => !e)}>
                  <TrunOnOffIcon />
                </SwitchButton>
              </div>
            </div>
          </div>
        </header>
        {open && (
          <div className="menu-select-product">
            <div>
              <h1 className="menu-select-product__title">{context.translations.soundbox.productSelectionTitle}</h1>
              {showTags && (
                <div className="menu-select-product__tags-wrapper">
                  <Tag
                    label={`${context.translations.soundbox.all} (${varaintCounts.all})`}
                    active={category === "all"}
                    onClick={() => setCategory("all")}
                    disabled={varaintCounts.all === 0}
                  />
                  <Tag
                    label={`${context.translations.soundbox.air} (${varaintCounts.air})`}
                    active={category === "air"}
                    onClick={() => setCategory("air")}
                    disabled={varaintCounts.air === 0}
                  />
                  <Tag
                    label={`${context.translations.soundbox.water} (${varaintCounts.water})`}
                    active={category === "water"}
                    onClick={() => setCategory("water")}
                    disabled={varaintCounts.water === 0}
                  />
                  <Tag
                    label={`${context.translations.soundbox.floor} (${varaintCounts.floor})`}
                    active={category === "floor"}
                    onClick={() => setCategory("floor")}
                    disabled={varaintCounts.floor === 0}
                  />
                </div>
              )}
              <Carousel
                items={heatPumps}
                translations={context.translations}
                category={category}
                setSelectedPump={setCarouselHeatPumpSelection}
                selectedPump={carouselHeatPumpSelection}
              />
              <div className="menu-select-product__button-wrapper">
                <Button
                  type="primary"
                  icon={<SoundMaxIcon />}
                  onClick={() => {
                    selectedHeatPumpContext.setData({
                      heatPumpId: carouselHeatPumpSelection.selectedHeatPumpId,
                      variantIndex: carouselHeatPumpSelection.variantIndex,
                    });
                    const heatPump = context.heatPumps.find(e => e.id === carouselHeatPumpSelection.selectedHeatPumpId)
                      ?.variants[carouselHeatPumpSelection.variantIndex];

                    if (heatPump) {
                      (window as any).dataLayer.push({
                        event: "hp_selection",
                        hp_model: heatPump.shortcut,
                        power_setting: heatPump.power + " kW",
                      });
                    }
                    setOpen(false);
                  }}
                >
                  {context.translations.soundbox.productSelectionButton}
                </Button>
              </div>
            </div>

            <div>
              <div className={"menu-select-product__links-wrapper"}>
                <div className={"menu-select-product__links-wrapper-item"}>
                  <TextButton
                    tag={{ type: "button" }}
                    text={context.translations.disclaimer.disclaimerButtonText}
                    onClick={() => {
                      setOpenDisclaimer(true);
                      setOpen(false);
                    }}
                  />
                </div>
                <div className={"menu-select-product__links-wrapper-item"}>
                  <TextButton
                    tag={{ type: "link", href: context.translations.impressum.impressumUrl }}
                    text={context.translations.impressum.impressumButtonText}
                  />
                </div>
                {availableLanguages.length > 1 && (
                  <div className="menu-select-product__links-wrapper-item">
                    <LanguagePicker type="grey" languages={availableLanguages} />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {open && <div className="black_blur"></div>}
      </div>

      {openDisclaimer && (
        <Disclaimer
          handleClose={() => {
            setOpen(true);
            setOpenDisclaimer(false);
          }}
        />
      )}
    </>
  );
});

const saunierLogo = (
  <svg
    version="1.1"
    id="Ebene_1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    x="0px"
    y="0px"
    viewBox="0 0 796 149"
    xmlSpace="preserve"
    width="100%"
    height="100%"
  >
    <g>
      <path
        fill="#CE0E2D"
        d="M158.7,34.9l-16.9-3.3c-1.1-2.1-2.5-4.1-4.2-6c-10.5-11.5-28.3-12.4-39.8-1.9c-8.7,7.9-11.3,20-7.6,30.4
   l-2.7,2.7C67.9,43,30.2,16.6,23.4,11.9c-4,7.5-3.7,20.3,6.8,27.6c8.3,5.8,29.4,20.6,43.9,30.8l-4.8,4.8
   C50.6,61.9,21,41.2,14.7,36.8c-4,7.5-3.7,20.3,6.8,27.6c7.2,5,22.2,15.5,34.4,24.1l-4.8,4.8C36,82.7,16.9,69.3,11.5,65.5
   C9.7,74,10.8,87.9,22.8,96.3c4.2,3,9.5,6.7,14.8,10.4l-18.3,18.2c0,0,25.9,7.9,60,7.9c37.6,0,67.9-30.4,67.9-67.9
   c0-6.7-0.6-12.7-1.2-17.3L158.7,34.9z M125,40.8c-2.4,0-4.3-2-4.3-4.4c0-2.4,2-4.3,4.4-4.3c2.4,0,4.3,2,4.3,4.4
   C129.3,38.9,127.3,40.8,125,40.8z"
      />
      <path
        fill="#101820"
        d="M778.9,39.3h-9.2V110h9.2V39.3z M255.7,96.4c0-3.9,2.7-6.2,7.7-7.1l14.4-2.7v1.7c0,8.8-5.8,14.3-14.4,14.3
   C258.6,102.6,255.7,100.1,255.7,96.4z M267.3,60.3c-9.3,0-15.9,4.7-19.7,10.6l6.9,4.8c2.5-3.8,6.6-6.7,12.5-6.7
   c6.1,0,10.6,3.9,10.8,10l-16.6,3.1c-9.6,1.8-15.1,6-15.1,14.7c0,8.3,6.3,14.2,15.9,14.2c7.7,0,13.1-3.4,16.1-8.7v7.7h8.9V78.5
   C287,67.7,278.7,60.3,267.3,60.3z M319,111c7.6,0,12.4-2.5,15.4-7.9v6.9h8.9V61.3h-9.2v26.5c0,8.6-5.4,14.5-13.6,14.5
   c-7.2,0-11.4-5.5-11.4-13v-28h-9.2v29C299.9,103.3,307.2,111,319,111z M503.2,83.4c0-8.5,6.3-13.9,13.5-13.9h3.9v-9.2h-2.9
   c-7.5,0-12.2,3.5-14.8,9v-8H494V110h9.2V83.4z M459.1,68.6c7.7,0,12.9,5.2,13.9,12.8h-28.1C446.2,74.2,451.6,68.6,459.1,68.6z
    M480.7,99.9l-7-4.9c-3,4.5-7.3,7.3-14.1,7.3c-7.6,0-13.9-6-14.9-13.2h37.6c0.3-1.3,0.5-2.9,0.5-5.1c0-13.1-9.8-23.7-23.6-23.7
   c-13.1,0-24.3,10.8-24.2,25.4c0.1,15.1,10.7,25.3,24.5,25.3C470.4,111,477.1,105.4,480.7,99.9z M423.4,61.3h-9.2V110h9.2V61.3z
    M418.8,52.1c-3.2,0-5.8-2.6-5.8-5.8c0-3.1,2.6-5.7,5.8-5.7c3.2,0,5.8,2.6,5.8,5.7C424.6,49.5,422,52.1,418.8,52.1z M366.6,83.4
   c0-8.6,5.8-14.4,13.5-14.4c6.8,0,11.6,5,11.6,12.5V110h9.2V81.5c0-13-7.2-21.2-19.1-21.2c-7.1,0-12.7,2.9-15.5,8v-7l-8.9,0V110
   l9.2,0V83.4z M633,111c7.6,0,12.4-2.5,15.4-7.9v6.9h8.9V61.3h-9.2v26.5c0,8.6-5.4,14.5-13.6,14.5c-7.2,0-11.4-5.5-11.4-13v-28h-9.2
   v29C613.9,103.3,621.2,111,633,111z M725.9,96.4c0-3.9,2.7-6.2,7.7-7.1l14.4-2.7v1.7c0,8.8-5.8,14.3-14.4,14.3
   C728.8,102.6,725.9,100.1,725.9,96.4z M737.5,60.3c-9.3,0-15.9,4.7-19.7,10.6l6.9,4.8c2.5-3.8,6.6-6.7,12.5-6.7
   c6.1,0,10.6,3.9,10.8,10l-16.6,3.1c-9.6,1.8-15.1,6-15.1,14.7c0,8.3,6.3,14.2,15.9,14.2c7.7,0,13.1-3.4,16.1-8.7v7.7h8.9V78.5
   C757.2,67.7,748.9,60.3,737.5,60.3z M693,110l20-48.7h-9.8l-14.4,36.6l-14.1-36.6h-10.1l19.9,48.7H693z M566,48.2
   c17.4,0,27,11.9,27,26.4s-9.6,26.3-27,26.3h-8.6V48.2H566z M602.8,74.6c0-19-13.3-35.3-36.2-35.3l-18.9,0V110l18.9,0
   C589.5,110,602.8,93.7,602.8,74.6z M212.9,101.8c9.2,0,14.3-4.6,14.3-11.5c0-7.7-7.9-9.4-16-11.8c-10.1-3.1-20.6-6.8-20.6-20.4
   c0-10.5,8.7-19.8,23.2-19.8c9.7,0,16.3,3.5,21.6,10l-6.7,6.6c-3.5-4.4-7.4-7.6-14.9-7.6c-8.5,0-13.5,5-13.5,10.8
   c0,7.2,6.9,9.1,14.2,11.3c10.7,3.2,22.6,6.7,22.6,20.9c0,11.8-8.6,20.7-24.2,20.7c-12.9,0-20-5-24.7-11.5l6.9-6.6
   C199.1,97.9,203.5,101.8,212.9,101.8z"
      />
    </g>
  </svg>
);

const vaillantLogo = (
  <svg width="100%" height="100%" viewBox="0 0 142 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M60.3577 29.5173V26.0476H58.1651C55.4552 26.0476 54.4452 26.7806 54.4452 28.7354C54.4452 30.3726 55.135 31.1056 56.6624 31.1056C58.2883 31.0812 59.1259 30.4459 60.3577 29.5173ZM60.7519 33.036V31.9853C58.5593 34.0134 56.8841 34.2333 55.2089 34.2333C51.9077 34.2333 50.0107 32.2296 50.0107 28.8087C50.0107 24.9969 52.4989 23.1642 57.6724 23.1642H60.3577V22.1868C60.3577 20.0366 59.594 19.328 57.2536 19.328C55.9233 19.328 54.322 19.4257 52.4989 19.8655C52.154 19.9388 51.8338 19.6945 51.8338 19.3524V17.1777C51.8338 16.8112 52.0801 16.4935 52.4497 16.3958C53.5829 16.127 55.4552 15.8093 58.2883 15.8093C63.1169 15.8093 64.7921 17.4465 64.7921 21.9669V33.0115C64.7921 33.378 64.4965 33.6713 64.127 33.6713H61.417C61.0475 33.6957 60.7519 33.4025 60.7519 33.036Z"
      fill="#00897D"
    />
    <path
      d="M102.731 29.5173V26.0475H100.538C97.8283 26.0475 96.8182 26.7806 96.8182 28.7354C96.8182 30.3725 97.508 31.1056 99.0354 31.1056C100.686 31.0811 101.524 30.4458 102.731 29.5173ZM103.125 33.0359V31.9852C100.957 34.0133 99.2571 34.2332 97.5819 34.2332C94.2807 34.2332 92.3838 32.2296 92.3838 28.8087C92.3838 24.9968 94.872 23.1642 100.021 23.1642H102.731V22.1868C102.731 20.0365 101.918 19.3279 99.5035 19.3279C98.1239 19.3279 96.3255 19.4745 94.4778 19.9144C94.1329 19.9877 93.8127 19.7433 93.8127 19.4012V17.2265C93.8127 16.86 94.059 16.5424 94.4285 16.469C95.6111 16.2003 97.6066 15.8337 100.514 15.8337C105.441 15.8337 107.165 17.4709 107.165 21.9913V33.0359C107.165 33.4025 106.87 33.6957 106.5 33.6957H103.79C103.445 33.6957 103.125 33.4025 103.125 33.0359Z"
      fill="#00897D"
    />
    <path
      d="M136.039 34.209C131.752 34.209 129.904 32.6452 129.904 28.1003V12.7307C129.904 12.3641 130.2 12.0709 130.569 12.0709H133.674C134.043 12.0709 134.339 12.3641 134.339 12.7307V16.347H139.315C139.685 16.347 139.98 16.6403 139.98 17.0068V19.3037C139.98 19.6702 139.685 19.9634 139.315 19.9634H134.339V28.0514C134.339 29.8107 135.102 30.6171 136.778 30.6171C138.059 30.6171 138.872 30.5437 139.611 30.4704C139.956 30.446 140.251 30.6904 140.251 31.0324V33.1583C140.251 33.5492 139.98 33.8669 139.586 33.9402C138.256 34.1601 137.517 34.209 136.039 34.209Z"
      fill="#00897D"
    />
    <path
      d="M114.95 17.0066V18.0329C116.65 16.6401 118.498 15.8337 120.74 15.8337C124.287 15.8337 126.16 17.3976 126.184 21.2094L126.234 33.0604C126.234 33.4269 125.938 33.7201 125.568 33.7201H122.415C122.045 33.7201 121.75 33.4269 121.75 33.0604V22.5534C121.75 20.3542 121.011 19.6456 119.163 19.6456C117.808 19.6456 116.7 20.0854 115.345 21.0873V33.0604C115.345 33.4269 115.049 33.7201 114.679 33.7201H111.575C111.206 33.7201 110.91 33.4269 110.91 33.0604V17.0066C110.91 16.6401 111.206 16.3469 111.575 16.3469H114.285C114.655 16.3469 114.95 16.6401 114.95 17.0066Z"
      fill="#00897D"
    />
    <path
      d="M85.1162 33.036V9.99385C85.1162 9.62733 85.4118 9.33411 85.7814 9.33411H88.8855C89.255 9.33411 89.5506 9.62733 89.5506 9.99385V33.0116C89.5506 33.3781 89.255 33.6713 88.8855 33.6713H85.7814C85.4118 33.6957 85.1162 33.4025 85.1162 33.036Z"
      fill="#00897D"
    />
    <path
      d="M76.7646 33.036V9.99385C76.7646 9.62733 77.0603 9.33411 77.4298 9.33411H80.5585C80.9281 9.33411 81.2237 9.62733 81.2237 9.99385V33.0116C81.2237 33.3781 80.9281 33.6713 80.5585 33.6713H77.4544C77.0603 33.6957 76.7646 33.4025 76.7646 33.036Z"
      fill="#00897D"
    />
    <path
      d="M68.5117 33.036V17.0067C68.5117 16.6401 68.8073 16.3469 69.1769 16.3469H72.281C72.6505 16.3469 72.9461 16.6401 72.9461 17.0067V33.036C72.9461 33.4025 72.6505 33.6957 72.281 33.6957H69.1769C68.8073 33.6957 68.5117 33.4025 68.5117 33.036Z"
      fill="#00897D"
    />
    <path
      d="M68.4375 12.8527V9.99385C68.4375 9.62733 68.7331 9.33411 69.1027 9.33411H72.3792C72.7488 9.33411 73.0444 9.62733 73.0444 9.99385V12.8527C73.0444 13.2193 72.7488 13.5125 72.3792 13.5125H69.1027C68.7331 13.5369 68.4375 13.2437 68.4375 12.8527Z"
      fill="#00897D"
    />
    <path
      d="M51.2419 10.0672L44.1714 33.1093C44.0729 33.4514 43.7526 33.6713 43.4077 33.6713H39.1211C38.7762 33.6713 38.4559 33.4514 38.3574 33.1093L31.3116 10.0672C31.1884 9.70063 31.484 9.33411 31.8536 9.33411H35.4257C35.7953 9.33411 36.1155 9.57846 36.1894 9.92054L41.3137 29.2241L46.4379 9.92054C46.5364 9.57846 46.8567 9.33411 47.2016 9.33411H50.6506C51.094 9.33411 51.365 9.72507 51.2419 10.0672Z"
      fill="#00897D"
    />
    <path
      d="M10.2483 6.01107C11.6279 5.22916 13.1553 4.76489 14.8059 4.76489C16.4565 4.76489 17.9593 5.22916 19.3389 6.01107C18.994 6.59751 18.6245 7.25726 18.2303 8.01474C17.1217 7.37943 15.9638 7.06178 14.8059 7.06178C13.6234 7.06178 12.4902 7.37943 11.3816 8.01474C10.9628 7.25726 10.5932 6.59751 10.2483 6.01107ZM23.4531 9.72519C23.1082 10.7026 22.7633 11.5089 22.3937 12.242C24.3646 15.3452 25.5224 19.2792 25.5224 22.8468C25.5224 26.3165 24.3892 29.713 22.3937 32.1076C20.9402 33.8669 18.452 35.9683 14.8059 35.9683C11.1599 35.9683 8.67165 33.8669 7.21814 32.1076C5.22265 29.6885 4.08941 26.3165 4.08941 22.8468C4.08941 19.2792 5.27192 15.3452 7.24278 12.242C6.84861 11.5089 6.52834 10.6781 6.18344 9.72519C3.37498 13.366 1.74902 18.424 1.74902 22.8468C1.74902 27.3428 3.37498 31.1302 5.41974 33.5737C7.71086 36.3348 10.8889 38.2652 14.8059 38.2652C18.723 38.2652 21.9256 36.3348 24.1921 33.5737C26.2122 31.1302 27.8628 27.3428 27.8628 22.8468C27.8382 18.424 26.2122 13.366 23.4531 9.72519Z"
      fill="#00897D"
    />
    <path
      d="M22.3934 12.2419C22.9354 11.1667 23.4035 9.96944 23.8962 8.38117C24.4628 6.57298 24.9309 3.46974 24.2164 2.3213C23.9701 1.93034 23.6252 1.78373 23.4035 1.7593C23.3295 1.73486 23.2803 1.73486 23.2064 1.73486C22.2209 1.73486 21.0384 2.78557 18.23 8.01464C17.1214 7.37933 15.9635 7.06168 14.8056 7.06168C13.6477 7.06168 12.4899 7.37933 11.3812 8.01464C8.57278 2.78557 7.39027 1.73486 6.38021 1.73486C6.3063 1.73486 6.25703 1.73486 6.18312 1.7593C5.9614 1.80817 5.6165 1.93034 5.37014 2.3213C4.65571 3.49418 5.12379 6.57298 5.69041 8.38117C6.18312 9.99387 6.6512 11.1667 7.19318 12.2419C5.22233 15.3451 4.06445 19.2791 4.06445 22.8466C4.06445 26.3408 5.19769 29.7129 7.19318 32.1075C8.64669 33.8668 11.1349 35.9682 14.781 35.9682C18.427 35.9682 20.9399 33.8668 22.3688 32.1075C24.3642 29.6884 25.4975 26.3164 25.4975 22.8466C25.5221 19.2791 24.3642 15.3451 22.3934 12.2419Z"
      fill="white"
    />
    <path
      d="M17.7873 21.8936C18.0583 21.5515 17.6395 21.3071 17.5163 21.2338C17.467 21.2094 17.467 21.1605 17.467 21.1117C17.7627 20.3786 18.4524 19.9877 18.9452 20.3053C19.364 20.5497 19.4379 21.5026 18.9452 22.4556C18.4771 23.3597 17.467 23.7018 17.0482 23.4574C16.7772 23.2619 16.728 22.8954 16.9989 22.0402C17.0236 21.9425 17.0729 21.918 17.1714 21.9669C17.2453 21.9913 17.5902 22.1379 17.7873 21.8936ZM23.6259 27.6847L18.8959 27.7091C18.9452 27.8801 18.9452 28.0756 18.9205 28.2711L23.6013 28.2955C23.7738 28.2955 23.8969 28.1489 23.8969 28.0023C23.9216 27.8068 23.7738 27.6847 23.6259 27.6847ZM24.365 26.5118C24.365 26.6584 24.2418 26.805 24.094 26.805L18.7973 27.0738C18.4771 26.2674 17.6641 26.3896 17.6641 25.7054C17.6641 25.3389 17.8612 25.1923 18.0336 24.9724C18.7973 24.0683 20.103 22.8221 20.2509 20.9895C20.3248 19.841 20.0538 18.3016 19.1176 18.1306C17.3438 17.7641 15.5701 21.7958 15.5701 24.3371C15.5701 25.3633 15.5454 25.681 15.9396 26.2919C16.1613 26.5851 16.4323 26.9761 16.3831 27.3914C16.3338 27.9046 15.8411 28.0512 15.5947 28.2955C14.8556 29.0042 15.6193 30.177 16.9743 30.177C17.8858 30.177 18.6249 29.7128 18.822 28.882L22.9854 29.1019C23.1332 29.1263 23.2564 29.2485 23.2564 29.3951C23.2564 29.5417 23.1086 29.6639 22.9608 29.6639L19.5857 29.444C18.6495 31.0811 15.8903 32.645 14.7571 32.645C13.6239 32.645 10.8893 31.0811 9.92851 29.444L6.55342 29.6639C6.40561 29.6639 6.2578 29.5662 6.2578 29.3951C6.23316 29.2485 6.38097 29.1263 6.52879 29.1019L10.6922 28.882C10.9139 29.7128 11.653 30.177 12.5645 30.177C13.9195 30.177 14.6832 29.0042 13.9441 28.2955C13.6978 28.0512 13.2051 27.9046 13.1558 27.3914C13.1065 27.0005 13.4021 26.6095 13.5992 26.2919C14.018 25.7054 13.9688 25.3633 13.9688 24.3371C13.9688 21.7958 12.195 17.7641 10.4212 18.1306C9.48507 18.3261 9.18944 19.841 9.28798 20.9895C9.4358 22.8221 10.7169 24.0683 11.4806 24.9724C11.6776 25.1923 11.8501 25.3389 11.8501 25.7054C11.8501 26.3896 11.0618 26.2674 10.7169 27.0738L5.42018 26.805C5.27237 26.805 5.12455 26.6584 5.14919 26.5118C5.14919 26.3652 5.297 26.243 5.44482 26.243L9.11553 26.4385C9.53434 24.0194 8.27792 20.8918 8.52428 18.8881C8.72136 17.2265 9.90387 15.8826 10.7908 15.2473C10.8893 15.174 10.9139 15.0518 10.8893 14.9296C10.569 13.8301 10.1995 11.9974 9.21408 9.57837C8.22865 7.13488 7.3664 4.71582 6.79978 4.76469C6.23316 4.83799 6.62733 7.18374 7.78521 10.1648C8.94308 13.1459 10.1749 14.4654 9.82997 14.6364C9.48507 14.8319 8.94308 14.0011 8.27792 12.8771C7.48958 11.5576 6.92296 10.1404 6.30707 8.18558C5.59263 5.86426 5.32164 2.63885 6.20852 2.49224C7.09541 2.34563 8.746 4.91129 10.7169 8.57654C12.6384 12.1929 13.3036 14.3187 13.3036 14.3187C13.3282 14.3921 13.3775 14.5142 13.55 14.4898C13.8949 14.4165 14.3137 14.3921 14.7078 14.3921C15.1266 14.3921 15.5208 14.4165 15.8657 14.4898C16.0382 14.5142 16.0874 14.4165 16.1121 14.3187C16.1121 14.3187 16.7772 12.1929 18.6742 8.57654C20.645 4.88686 22.2956 2.34563 23.1825 2.49224C24.1433 2.61441 23.8969 5.86426 23.1825 8.16114C22.5666 10.1404 22 11.5332 21.2116 12.8527C20.5465 13.9767 20.0045 14.783 19.6596 14.612C19.3393 14.4409 20.5711 13.1214 21.7044 10.1404C22.8622 7.15931 23.2564 4.81356 22.6898 4.74025C22.1232 4.69138 21.2609 7.11044 20.2755 9.55393C19.2901 11.973 18.9205 13.8056 18.6003 14.9052C18.5756 15.0274 18.6003 15.1495 18.6988 15.2228C19.5857 15.8582 20.7682 17.2021 20.9653 18.8636C21.2116 20.8673 19.9552 23.995 20.374 26.414L24.0447 26.2186C24.2172 26.243 24.365 26.3652 24.365 26.5118ZM14.8064 30.2748C14.4861 30.6169 13.9688 30.9834 13.4021 31.1544C13.7963 31.5454 14.3137 31.7409 14.8064 31.7409C15.3237 31.7409 15.8164 31.5454 16.2106 31.1544C15.6193 30.9834 15.102 30.6169 14.8064 30.2748ZM12.0718 21.2583C12.1211 21.2338 12.1211 21.185 12.1211 21.1361C11.8255 20.4031 11.1357 20.0121 10.6429 20.3297C10.2241 20.5741 10.1502 21.5271 10.6429 22.48C11.111 23.3841 12.1457 23.7262 12.5399 23.4819C12.8109 23.2864 12.8602 22.9199 12.5892 22.0646C12.5645 21.9669 12.5153 21.9425 12.4167 21.9913C12.3674 22.0158 11.9979 22.1379 11.8255 21.918C11.5545 21.5515 11.9486 21.3071 12.0718 21.2583ZM10.6922 27.6847L5.9868 27.6602C5.83899 27.6602 5.69118 27.8068 5.69118 27.9535C5.69118 28.1001 5.83899 28.2467 5.9868 28.2467L10.6676 28.2222C10.6429 28.0512 10.6676 27.8801 10.6922 27.6847Z"
      fill="black"
      fillOpacity="0.8"
    />
  </svg>
);
